/**
 * Customer PWA feature flags — deferred v1.1 capabilities only.
 * Retail shell, shop categories v2, social OIDC, and PWA install are always on.
 */

export interface CustomerFeatureFlags {
  /** Account — list/revoke active refresh sessions per device. */
  readonly customerAccountActiveSessionsV1: boolean;
  /** Payment v1.1 — wallet rails (Apple/Google Pay). */
  readonly customerWalletPaymentsV1_1: boolean;
  /**
   * Kupóny hub — account home + bottom-nav / Více overview item.
   * Default OFF → Kupóny hidden from overflow / account-home hub.
   * ON → Kupóny overview in overflow / account-home hub.
   */
  readonly customerKuponyEnabled: boolean;
  /**
   * Oblíbené — FR-02 More drawer + desktop sidebar hub; V1B primary (browse OFF).
   * Never a V1A (browse-on) bottom-nav primary (FR-01 hard gate). Default OFF.
   */
  readonly customerFavoritesNavEnabled: boolean;
  /**
   * Obchody `/shops` — FR-02 More drawer + desktop sidebar; V1B shops-append
   * primary ORDER Oblíbené|Skenovat|Objednávky|Obchody (browse OFF + scan ON).
   * Never a V1A (browse-on) bottom-nav primary (FR-01). Default OFF.
   */
  readonly customerShopsNavEnabled: boolean;
  /** Desktop sidebar Věrnost entry (/account/orders?tab=loyalty). */
  readonly customerLoyaltyNavEnabled: boolean;
  /**
   * Nakupovat — platform `/browse` discovery + bottom-nav / sidebar entry.
   * V1A hard gate: browse ON → bottom-nav primaries Nakupovat|Skenovat|Objednávky (G22 soft residual).
   * Default ON — only literal env `false` disables (boot-only).
   */
  readonly customerBrowseNavEnabled: boolean;
  /**
   * Skenovat — platform `/scan` + bottom-nav / sidebar entry.
   * Default ON — only literal env `false` disables (boot-only).
   */
  readonly customerScanNavEnabled: boolean;
  /** Gift rollout master — gates all donation surfaces when false. */
  readonly customerGiftSurfacesMasterEnabled: boolean;
  /** Darovat menu + donation checkout entry. */
  readonly customerGiftDonateMenuEnabled: boolean;
  /** Trvalé příkazy (recurring donations). */
  readonly customerGiftRecurringDonationsEnabled: boolean;
  /** Moje dary (donation history). */
  readonly customerGiftMyDonationsEnabled: boolean;
}

export const DEFAULT_CUSTOMER_FEATURE_FLAGS: CustomerFeatureFlags = {
  customerAccountActiveSessionsV1: false,
  customerWalletPaymentsV1_1: false,
  customerKuponyEnabled: false,
  customerFavoritesNavEnabled: false,
  customerShopsNavEnabled: false,
  customerLoyaltyNavEnabled: false,
  customerBrowseNavEnabled: true,
  customerScanNavEnabled: true,
  customerGiftSurfacesMasterEnabled: false,
  customerGiftDonateMenuEnabled: false,
  customerGiftRecurringDonationsEnabled: false,
  customerGiftMyDonationsEnabled: false,
};

export function resolveCustomerFeatureFlags(
  partial?: Partial<CustomerFeatureFlags>,
): CustomerFeatureFlags {
  return {
    customerAccountActiveSessionsV1:
      partial?.customerAccountActiveSessionsV1 ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerAccountActiveSessionsV1,
    customerWalletPaymentsV1_1:
      partial?.customerWalletPaymentsV1_1 ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerWalletPaymentsV1_1,
    customerKuponyEnabled:
      partial?.customerKuponyEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerKuponyEnabled,
    customerFavoritesNavEnabled:
      partial?.customerFavoritesNavEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerFavoritesNavEnabled,
    customerShopsNavEnabled:
      partial?.customerShopsNavEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerShopsNavEnabled,
    customerLoyaltyNavEnabled:
      partial?.customerLoyaltyNavEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerLoyaltyNavEnabled,
    customerBrowseNavEnabled:
      partial?.customerBrowseNavEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerBrowseNavEnabled,
    customerScanNavEnabled:
      partial?.customerScanNavEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerScanNavEnabled,
    customerGiftSurfacesMasterEnabled:
      partial?.customerGiftSurfacesMasterEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerGiftSurfacesMasterEnabled,
    customerGiftDonateMenuEnabled:
      partial?.customerGiftDonateMenuEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerGiftDonateMenuEnabled,
    customerGiftRecurringDonationsEnabled:
      partial?.customerGiftRecurringDonationsEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerGiftRecurringDonationsEnabled,
    customerGiftMyDonationsEnabled:
      partial?.customerGiftMyDonationsEnabled ??
      DEFAULT_CUSTOMER_FEATURE_FLAGS.customerGiftMyDonationsEnabled,
  };
}

/** Effective sub-flag = master && subFlag (poll merge applied upstream). */
export function resolveEffectiveGiftSubFlags(
  flags: CustomerFeatureFlags,
): Pick<
  CustomerFeatureFlags,
  | 'customerGiftDonateMenuEnabled'
  | 'customerGiftRecurringDonationsEnabled'
  | 'customerGiftMyDonationsEnabled'
> {
  const master = flags.customerGiftSurfacesMasterEnabled;
  return {
    customerGiftDonateMenuEnabled: master && flags.customerGiftDonateMenuEnabled,
    customerGiftRecurringDonationsEnabled: master && flags.customerGiftRecurringDonationsEnabled,
    customerGiftMyDonationsEnabled: master && flags.customerGiftMyDonationsEnabled,
  };
}

/**
 * Boot-only gift donate gate (no tenant poll/entitlement).
 * Used on platform `/` where TenantProvider / CSP are not mounted.
 * Default OFF — both master and donate sub-flag must be true.
 */
export function isBootGiftDonateMenuEnabled(flags: CustomerFeatureFlags): boolean {
  return (
    flags.customerGiftSurfacesMasterEnabled && flags.customerGiftDonateMenuEnabled
  );
}

/** Boot-only Nakupovat /browse gate — default ON unless flag explicitly false. */
export function isCustomerBrowseEnabled(flags: CustomerFeatureFlags): boolean {
  return flags.customerBrowseNavEnabled === true;
}

/** Boot-only Skenovat /scan gate — default ON unless flag explicitly false. */
export function isCustomerScanEnabled(flags: CustomerFeatureFlags): boolean {
  return flags.customerScanNavEnabled === true;
}

export const DEV_FEATURE_FLAGS_STORAGE_KEY = 'rpapp-customer.dev.flags';

export function readDevFeatureFlagOverrides(): Partial<CustomerFeatureFlags> | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (process.env.NODE_ENV === 'production') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(DEV_FEATURE_FLAGS_STORAGE_KEY);
    if (raw === null || raw.trim().length === 0) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<CustomerFeatureFlags>;
    return parsed;
  } catch {
    return null;
  }
}
